import React from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  loadOfferFinancingStructure,
  saveOfferFinancingStructure,
  saveOfferNegotiationAmount,
  computeMaxResidualValuePercent,
  cancelFinancingStructureChanges
} from 'actions/actionCreators/offer/financials/offerFinancingStructureActions';
import { loadInterestTypes, loadReferenceInterests, loadContractTypes, loadPriceTypes } from 'actions/actionCreators/dictionariesActions';
import { isMandatory, isInRange } from 'utils/simple-validations';
import OfferFinancingStructureCard from './OfferFinancingStructureCard';
import OfferNegotiationModal from './OfferNegotiationModal';
import * as editableDocument from 'utils/model/editableDocument';
import flow from 'lodash.flow';
import { when, valueChanged } from 'utils/fn-combinators';
import debounce from 'lodash.debounce';
import { loadOfferFinancialProductRuleReadOnlyFields } from 'actions/actionCreators/offer/offerActions';
import { withParams } from 'providers/ParamsConsumer';
import { getIsContractModification, getIsFacilityFrame, getPriceTypes } from 'selectors/dictionariesSelectors';
import { getOfferType, getDocumentType } from 'features/offer/edit/client/form/selectors';
import OfferType from 'constants/offerType';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { LEASING_OFFER_CHANGED } from 'features/offer/edit/insurances/subscriptions';
import { round } from 'lodash';

const getValidationRules = (document, cvsChAmount) => ({
  advance: [
    isMandatory(),
    isInRange(
      document => round((document.minAdvancePercent * document.totalAcquisitionPrice) / 100, cvsChAmount),
      document => round((document.maxAdvancePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
    )
  ],
  advancePercent: [
    isMandatory(),
    isInRange(
      document => document.minAdvancePercent,
      document => document.maxAdvancePercent
    )
  ],
  residualValue: [
    !document.hideInfoForDealersByContractTypes && isMandatory(),
    isInRange(
      document =>
        !document.hideInfoForDealersByContractTypes
          ? round((document.minResidualValuePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
          : 0,
      document => round((document.maxResidualValuePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
    )
  ],
  residualValuePercent: [
    !document.hideInfoForDealersByContractTypes && isMandatory(),
    isInRange(
      document => (!document.hideInfoForDealersByContractTypes ? document.minResidualValuePercent : 0),
      document => document.maxResidualValuePercent
    )
  ],
  durationMonths: [
    isMandatory(),
    isInRange(
      document => document.minDurationMonths,
      document => document.maxDurationMonths
    )
  ],
  numberOfGuaranteeRates: [
    isInRange(
      document => document.minNumberOfGuaranteeRates,
      document => document.maxNumberOfGuaranteeRates
    )
  ],
  managementFee: [
    isMandatory(),
    isInRange(0, document => round((document.maxManagementFeePercent * document.totalAcquisitionPrice) / 100, cvsChAmount))
  ],
  logisticTax: {
    value: [document && document.logisticTax?.isMandatory && isMandatory()]
  }
});

const getBusinessRules = (cvsChAmount, cvsChPercent) => ({
  advancePercent: when(
    valueChanged(document => document.advance),
    document => round(document.advance && (document.advance / document.totalAcquisitionPrice) * 100, cvsChPercent)
  ),
  advance: when(
    valueChanged(document => document.advancePercent),
    document => round(document.advancePercent && (document.advancePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
  ),
  financingValuePercent: when(
    valueChanged(document => document.advancePercent),
    document => round((document.advancePercent ? 100 - document.advancePercent : 100), cvsChPercent)
  ),

  financingValue: when(
    valueChanged(document => document.advance),
    document => round(document.advance ? document.totalAcquisitionPrice - document.advance : document.totalAcquisitionPrice, cvsChAmount)
  ),
  residualValue: when(
    valueChanged(document => document.residualValuePercent),
    document => round((document.residualValuePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
  ),
  residualValuePercent: when(
    valueChanged(document => document.residualValue),
    document => round((document.residualValue / document.totalAcquisitionPrice) * 100, cvsChPercent)
  ),

  managementFee: when(
    valueChanged(document => document.managementFeePercent),
    document => round(document.managementFeePercent && (document.managementFeePercent * document.totalAcquisitionPrice) / 100, cvsChAmount)
  ),

  managementFeePercent: when(
    valueChanged(document => document.managementFee),
    document => round(document.managementFee && (document.managementFee / document.totalAcquisitionPrice) * 100, cvsChPercent)
  )
});

const setDocumentDefaults = document => {
  return Object.assign({}, document, {
    minAdvancePercent: document.minAdvancePercent || 0,
    maxAdvancePercent: document.maxAdvancePercent || 100,
    durationMonths: document.durationMonths || 0,
    minDurationMonths: document.minDurationMonths || 0,
    maxDurationMonths: document.maxDurationMonths || 120,
    minResidualValuePercent: document.minResidualValuePercent || 0,
    maxResidualValuePercent: document.maxResidualValuePercent || 100,
    minNumberOfGuaranteeRates: document.minNumberOfGuaranteeRates || 0,
    maxNumberOfGuaranteeRates: document.maxNumberOfGuaranteeRates || 100,
    minManagementFeePercent: document.minManagementFeePercent || 0,
    maxManagementFeePercent: document.maxManagementFeePercent || 100
  });
};

/* eslint react/prop-types: 0 */
const InsuranceSubscriptionComponent = ({ offerId, offerSiteId }) => {
  useGlobalSubscription(LEASING_OFFER_CHANGED, { variables: { documentId: offerId, siteId: offerSiteId } });
  return null;
};
InsuranceSubscriptionComponent.propTypes = {
  offerId: PropTypes.number.isRequired,
  offerSiteId: PropTypes.number.isRequired
};

InsuranceSubscriptionComponent.propTypes = {
  offerId: PropTypes.number.isRequired,
  offerSiteId: PropTypes.number.isRequired
};

class OfferFinancingStructureContainer extends React.PureComponent {
  state = {
    modifiedProp: {},
    financingStructure: null,
    openNegotiationModal: false,
    computeNegotiationInProgress: false,
    prevProps: {
      financingStructure: null,
      maxResidualValuePercent: null
    }
  };

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;
    const { params } = props;

    if (props.financingStructure !== prevProps.financingStructure) {
      return {
        prevProps: {
          financingStructure: props.financingStructure,
          maxResidualValuePercent: props.maxResidualValuePercent
        },
        financingStructure: flow(setDocumentDefaults, editableDocument.create)(props.financingStructure)
      };
    }
    if (props.maxResidualValuePercent !== prevProps.maxResidualValuePercent) {
      return {
        prevProps: { ...prevProps, maxResidualValuePercent: props.maxResidualValuePercent },
        financingStructure: editableDocument.update(
          'maxResidualValuePercent',
          props.maxResidualValuePercent,
          getBusinessRules(params.cvsChAmount, params.cvsChPercent),
          state.financingStructure
        )
      };
    }
    return null;
  }

  componentDidMount() {
    this.props.actions.loadOfferFinancingStructure(this.props.documentId, this.props.siteId);
    this.props.actions.loadOfferFinancialProductRuleReadOnlyFields(this.props.documentId, this.props.siteId);
    this.props.actions.loadInterestTypes();
    this.props.actions.loadReferenceInterests();
    this.props.actions.loadContractTypes();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.financingStructure !== this.props.financingStructure ||
      prevProps.documentId !== this.props.documentId ||
      prevProps.siteId !== this.props.siteId
    ) {
      this.props.actions.loadOfferFinancingStructure(this.props.documentId, this.props.siteId);
      this.props.actions.loadPriceTypes();
    }

    if (!this.props.offerFinancialProductRuleReadOnlyFields.isLoaded)
      this.props.actions.loadOfferFinancialProductRuleReadOnlyFields(this.props.documentId, this.props.siteId);
  }

  componentWillUnmount() {
    this.props.actions.cancelFinancingStructureChanges(this.props.documentId, this.props.siteId);
  }

  callBackPropertyChange = prop => {
    switch (prop) {
      case 'durationMonths':
        this.computeMaxResidualValuePercent();
        break;
      default:
        break;
    }
  };

  handlePropertyChange = params => prop => value => {
    const { cvsChAmount, cvsChPercent } = params;
    switch (prop) {
      case 'isFixed': {
        const fixedInterestId = this.props.interestTypes.find(int => int.code === 'INT_FIXED')?.id;
        this.setState(
          currentState => ({
            financingStructure: flow(
              editableDocument.update('isFixed', value === fixedInterestId ? true : false, getBusinessRules(cvsChAmount, cvsChPercent)),
              editableDocument.update('interestTypeId', null, getBusinessRules(cvsChAmount, cvsChPercent)),
              editableDocument.validate(getValidationRules(this.state.financingStructure.document, cvsChAmount), false)
            )(currentState.financingStructure)
          }),
          this.callBackPropertyChange(prop)
        );
        break;
      }
      case 'contractTypeId': {
        this.setState(
          currentState => ({
            financingStructure: flow(
              editableDocument.update('contractTypeId', value.contractTypeId, getBusinessRules(cvsChAmount, cvsChPercent)),
              editableDocument.update('contractTypeName', value.contractTypeName, getBusinessRules(cvsChAmount, cvsChPercent)),
              editableDocument.validate(getValidationRules(this.state.financingStructure.document, cvsChAmount), false)
            )(currentState.financingStructure)
          }),
          this.callBackPropertyChange(prop)
        );
        break;
      }
      default: {
        this.setState(
          currentState => ({
            financingStructure: flow(
              editableDocument.update(prop, value, getBusinessRules(cvsChAmount, cvsChPercent)),
              editableDocument.validate(getValidationRules(this.state.financingStructure.document, cvsChAmount), false)
            )(currentState.financingStructure)
          }),
          this.callBackPropertyChange(prop)
        );
      }
    }
  };

  computeMaxResidualValuePercent = debounce(() => {
    this.props.actions.computeMaxResidualValuePercent(this.props.documentId, this.props.siteId, this.state.financingStructure.document);
  }, 1000);

  handleSave = cvsChAmount => () => {
    this.setState(
      prevState => {
        const financingStructure = editableDocument.validate(
          getValidationRules(this.state.financingStructure.document, cvsChAmount),
          false,
          prevState.financingStructure
        );
        if (!financingStructure.validationInfo.isValid) {
          return { financingStructure };
        }
      },
      () => {
        if (this.state.financingStructure.validationInfo.isValid) {
          this.props.actions.saveOfferFinancingStructure(this.props.documentId, this.props.siteId, this.state.financingStructure.document);
        }
      }
    );
  };

  onOpenNegotiationModal = () => {
    this.setState({
      openNegotiationModal: true
    });
  };

  onCloseNegotiationModal = () => {
    this.setState({
      openNegotiationModal: false
    });
  };

  onSaveNegotiation = async value => {
    this.setState({
      computeNegotiationInProgress: true
    });
    const event = await this.props.actions.saveOfferNegotiationAmount(this.props.documentId, this.props.siteId, value);
    if (event) {
      this.setState({
        openNegotiationModal: false,
        computeNegotiationInProgress: false
      });
    }
  };

  getOfferFinancialProductVisibilityFields = params => {
    if (params.financialProductVisibilityFields) {
      try {
        const result = JSON.parse(params.financialProductVisibilityFields);
        return result;
      } catch {
        // Catch Json errors
      }
    }
    return [];
  };

  areGuaranteeRatesVisible = params => {
    const guaranteeRatesProperty = this.getOfferFinancialProductVisibilityFields(params).find(
      vf => vf.propertyName.trim().toUpperCase() === 'GuaranteeRates'.trim().toUpperCase()
    );

    return guaranteeRatesProperty ? guaranteeRatesProperty.isVisible : false;
  };

  render() {
    const {
      financingStructureLoading,
      maxResidualValuePercentLoading,
      saving,
      params,
      offerFinancialProductRuleReadOnlyFields,
      isContractModification,
      isFacilityFrameOffer,
      interestTypes,
      interestTypesIsLoaded,
      contractTypes,
      referenceInterests,
      priceTypes,
      webInsuranceIntegration,
      offerTypeId
    } = this.props;
    const { document, dirtyInfo, validationInfo } = this.state.financingStructure;
    return (
      <>
        {webInsuranceIntegration && offerTypeId === OfferType.restructuring.id && (
          <InsuranceSubscriptionComponent offerId={this.props.documentId} offerSiteId={this.props.siteId} />
        )}
        <OfferFinancingStructureCard
          financingStructure={document}
          isDirty={dirtyInfo.isDirty}
          validationInfo={validationInfo}
          financingStructureLoading={financingStructureLoading}
          maxResidualValuePercentLoading={maxResidualValuePercentLoading}
          offerFinancialProductRuleReadOnlyFields={offerFinancialProductRuleReadOnlyFields}
          onPropertyChange={this.handlePropertyChange(params)}
          onSave={this.handleSave(params.cvsChAmount)}
          saving={saving}
          onOpenNegotiationModal={this.onOpenNegotiationModal}
          areGuaranteeRatesVisible={this.areGuaranteeRatesVisible(params)}
          isContractModification={isContractModification}
          isFacilityFrameOffer={isFacilityFrameOffer}
          interestTypes={interestTypes}
          interestTypesIsLoaded={interestTypesIsLoaded}
          contractTypes={contractTypes}
          referenceInterests={referenceInterests}
          priceTypes={priceTypes}
          documentId={this.props.documentId}
          siteId={this.props.siteId}
        />
        {this.state.openNegotiationModal && (
          <OfferNegotiationModal
            financingStructure={document}
            computeNegotiationInProgress={this.state.computeNegotiationInProgress}
            onCloseNegotiationModal={this.onCloseNegotiationModal}
            onSaveNegotiation={this.onSaveNegotiation}
          />
        )}
      </>
    );
  }
}

OfferFinancingStructureContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  financingStructure: PropTypes.object.isRequired,
  maxResidualValuePercent: PropTypes.number,
  financingStructureLoading: PropTypes.bool.isRequired,
  maxResidualValuePercentLoading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  isContractModification: PropTypes.bool.isRequired,
  isFacilityFrameOffer: PropTypes.bool.isRequired,
  interestTypes: PropTypes.array,
  interestTypesIsLoaded: PropTypes.bool,
  referenceInterests: PropTypes.array,
  priceTypes: PropTypes.array,
  contractTypes: PropTypes.array,
  params: PropTypes.object,
  webInsuranceIntegration: PropTypes.bool.isRequired,
  offerTypeId: PropTypes.number.isRequired
};

function mapStateToProps(state) {
  const offerTypeId = getOfferType(state);
  const documentTypeId = getDocumentType(state);

  return {
    financingStructure: state.currentOffer.financingStructureData.financingStructure,
    maxResidualValuePercent:
      state.currentOffer.financingStructureData.maxResidualValuePercent ||
      state.currentOffer.financingStructureData.financingStructure.maxResidualValuePercent,
    financingStructureLoading: !state.currentOffer.financingStructureData.financingStructureLoaded,
    maxResidualValuePercentLoading: state.currentOffer.financingStructureData.maxResidualValuePercentLoading,
    saving: state.currentOffer.financingStructureData.saving,
    offerFinancialProductRuleReadOnlyFields: state.currentOffer.offerFinancialProductRuleReadOnlyFields,
    isContractModification: getIsContractModification(offerTypeId),
    isFacilityFrameOffer: getIsFacilityFrame(documentTypeId),
    interestTypes: state.dictionaries.interestTypes.isLoaded ? state.dictionaries.interestTypes.items : [],
    interestTypesIsLoaded: state.dictionaries.interestTypes && state.dictionaries.interestTypes.isLoaded,
    referenceInterests: state.dictionaries.referenceInterests.isLoaded ? state.dictionaries.referenceInterests.items : [],
    contractTypes: state.dictionaries.contractTypes.isLoaded ? state.dictionaries.contractTypes.items : [],
    priceTypes: getPriceTypes(state),
    offerTypeId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadOfferFinancingStructure,
        saveOfferFinancingStructure,
        saveOfferNegotiationAmount,
        computeMaxResidualValuePercent,
        cancelFinancingStructureChanges,
        loadOfferFinancialProductRuleReadOnlyFields,
        loadInterestTypes,
        loadReferenceInterests,
        loadContractTypes,
        loadPriceTypes
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(withParams(OfferFinancingStructureContainer));
